import type { UploadFile } from '@pflegenavi/shared-frontend/platform';
import type {
  Gender,
  LineItemConfigurationResultDto,
  OnboardingControllerResultTypes,
  ServiceProviderControllerResultTypes,
  TaxCodeResultDto,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { ConnectionStatus } from '@pflegenavi/service-provider-shared/api-endpoints';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import {
  useGetNursingHomeList,
  useGetResidentList,
  useGetTaxCodesByCountry,
  useGetTaxCodesByCountryPhoenix,
  useGetTaxLineConfigurations,
  useGetTaxLineConfigurationsPhoenix,
  useServiceProvider,
  useUploadReceiptImage,
} from '@pflegenavi/service-provider-frontend/api';
import { v4 } from 'uuid';
import { computeMd5HashOfFile } from '@pflegenavi/shared-web/hash-utils';
import { useInitializeResidents } from '../../hooks/useUnitializeResidents';
import { useGetCountryOfServiceProvider } from '../../hooks/useGetCountryOfServiceProvider';
import type { ReceiptBatchFormValues } from './interfaces/ReceiptBatchFormValues';
import type { FormikContextType } from 'formik';
import { useGetGlobalServiceProviderId } from '../../hooks/useGetGlobalServiceProviderId';
import { useShallow } from 'zustand/react/shallow';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';

interface ReceiptBatchStore {
  uploadReceiptImage: UploadFile;
  residents: Array<{
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    gender: Gender;
  }>;
  isLoading: boolean;
  taxConfigurations: TaxCodeResultDto[];
  lineItemConfigurations: LineItemConfigurationResultDto[];
  serviceProvider:
    | ServiceProviderControllerResultTypes['getServiceProvider']
    | undefined;
  formik: FormikContextType<ReceiptBatchFormValues> | undefined;
  setFormik: (formik: FormikContextType<ReceiptBatchFormValues>) => void;
  nursingHomes: OnboardingControllerResultTypes['getNursingHomeList'];
}

const ReceiptBatchContext = createContext<StoreApi<ReceiptBatchStore>>(
  undefined as any
);

// eslint-disable-next-line complexity
export const ReceiptBatchProvider: FC<PropsWithChildren> = ({ children }) => {
  useInitializeResidents();

  const { globalServiceProviderId } = useGetGlobalServiceProviderId();

  const store = useMemo(
    () =>
      createStore<ReceiptBatchStore>((set) => ({
        uploadReceiptImage: () => {
          return Promise.resolve(undefined);
        },
        residents: [],
        isLoading: false,
        lineItemConfigurations: [],
        taxConfigurations: [],
        formik: undefined,
        serviceProvider: undefined,
        setFormik: (formik) => set({ formik }),
        nursingHomes: [],
      })),
    []
  );

  const { data: nursingHomes } = useGetNursingHomeList();

  const [nursingHomeId] = useStore(
    store,
    useShallow((state) => [state.formik?.values?.nursingHomeId])
  );

  const selectedNursingHome = nursingHomes?.find(
    (n) => n.nursingHomeId === nursingHomeId
  );

  const { upload } = useUploadReceiptImage({
    tenant: selectedNursingHome?.tenant ?? '',
    uuid: v4,
    computeMd5Hash: computeMd5HashOfFile,
  });

  const { data: residents, isLoading: isLoadingResidents } = useGetResidentList(
    {
      nursingHomeId: selectedNursingHome?.nursingHomeId ?? '',
      globalServiceProviderId: globalServiceProviderId!,
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: 0,
      refetchIntervalInBackground: false,
      enabled:
        !!globalServiceProviderId && !!selectedNursingHome?.nursingHomeId,
    }
  );

  const countryId = useGetCountryOfServiceProvider();
  const usePhoenix = useFeatureFlagEnabled(FeatureFlag.dev_ReceiptBatchPhoenix);
  const { data: taxConfigurations, isLoading: isLoadingTaxConfigurations } = (
    usePhoenix ? useGetTaxCodesByCountryPhoenix : useGetTaxCodesByCountry
  )(countryId);
  const {
    data: lineItemConfigurations,
    isLoading: isLoadingLineItemConfigurations,
  } = (
    usePhoenix
      ? useGetTaxLineConfigurationsPhoenix
      : useGetTaxLineConfigurations
  )(globalServiceProviderId);

  const { data: serviceProvider, isLoading: isLoadingServiceProvider } =
    useServiceProvider({
      globalServiceProviderId,
    });

  useEffect(() => {
    store.setState({
      isLoading:
        isLoadingResidents ||
        isLoadingTaxConfigurations ||
        isLoadingLineItemConfigurations ||
        isLoadingServiceProvider,
    });
  }, [
    isLoadingLineItemConfigurations,
    isLoadingResidents,
    isLoadingTaxConfigurations,
    isLoadingServiceProvider,
    store,
  ]);

  useEffect(() => {
    const transformed = (residents ?? []).map((resident) => ({
      id: resident.id,
      name: `${resident.firstName} ${resident.lastName}`,
      firstName: resident.firstName,
      lastName: resident.lastName,
      gender: resident.gender,
    }));
    store.setState({ residents: transformed });
  }, [residents, store]);

  useEffect(() => {
    store.setState({ uploadReceiptImage: upload });
  }, [upload, store]);

  useEffect(() => {
    store.setState({ taxConfigurations: taxConfigurations ?? [] });
  }, [taxConfigurations, store]);

  useEffect(() => {
    store.setState({ lineItemConfigurations: lineItemConfigurations ?? [] });
  }, [lineItemConfigurations, store]);

  useEffect(() => {
    if (serviceProvider) {
      store.setState({ serviceProvider });
    }
  }, [serviceProvider, store]);

  useEffect(() => {
    const activeNursingHomes = (nursingHomes ?? []).filter(
      (n) => n.connectionStatus === ConnectionStatus.Active
    );

    store.setState({ nursingHomes: activeNursingHomes });
  }, [nursingHomes, store]);

  return (
    <ReceiptBatchContext.Provider value={store}>
      {children}
    </ReceiptBatchContext.Provider>
  );
};

export function useReceiptBatchStore<T>(
  selector: (state: ReceiptBatchStore) => T
): T {
  const store = useContext(ReceiptBatchContext);
  return useStore(store, selector);
}
